/**
 * Video Annotations Hook
 *
 * CRUD operations for video annotations - canvas-based
 * form feedback and coaching annotations on videos.
 */

import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";
import { toast } from "sonner";
import type { AnnotationData } from "@/components/video-coaching/VideoAnnotationCanvas";

export interface VideoAnnotation {
  id: string;
  organization_id: string;
  coach_id: string;
  client_id: string;
  library_video_id: string | null;
  message_id: string | null;
  source_video_url: string | null;
  title: string | null;
  annotation_data: AnnotationData;
  timestamp_start: number | null;
  timestamp_end: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateAnnotationInput {
  client_id: string;
  library_video_id?: string;
  message_id?: string;
  source_video_url?: string;
  title?: string;
  annotation_data: AnnotationData;
  timestamp_start?: number;
  timestamp_end?: number;
}

export interface UpdateAnnotationInput {
  title?: string;
  annotation_data?: AnnotationData;
  timestamp_start?: number;
  timestamp_end?: number;
}

export interface UseVideoAnnotationsReturn {
  annotations: VideoAnnotation[];
  loading: boolean;
  error: string | null;
  fetchAnnotations: (clientId?: string) => Promise<void>;
  fetchAnnotationsByVideo: (videoId: string) => Promise<VideoAnnotation[]>;
  createAnnotation: (input: CreateAnnotationInput) => Promise<VideoAnnotation | null>;
  updateAnnotation: (id: string, input: UpdateAnnotationInput) => Promise<VideoAnnotation | null>;
  deleteAnnotation: (id: string) => Promise<boolean>;
  getAnnotation: (id: string) => Promise<VideoAnnotation | null>;
}

export function useVideoAnnotations(): UseVideoAnnotationsReturn {
  const { organization } = useOrganization();
  const [annotations, setAnnotations] = useState<VideoAnnotation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all annotations (optionally by client)
  const fetchAnnotations = useCallback(
    async (clientId?: string) => {
      if (!organization?.id) return;

      setLoading(true);
      setError(null);

      try {
        let query = supabase
          .from("video_annotations")
          .select("*")
          .eq("organization_id", organization.id)
          .order("created_at", { ascending: false });

        if (clientId) {
          query = query.eq("client_id", clientId);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) throw fetchError;

        setAnnotations((data as VideoAnnotation[]) || []);
      } catch (err: any) {
        console.error("[useVideoAnnotations] Error fetching annotations:", err);
        setError(err.message || "Failed to fetch annotations");
      } finally {
        setLoading(false);
      }
    },
    [organization?.id]
  );

  // Fetch annotations for a specific video
  const fetchAnnotationsByVideo = useCallback(
    async (videoId: string): Promise<VideoAnnotation[]> => {
      try {
        const { data, error: fetchError } = await supabase
          .from("video_annotations")
          .select("*")
          .eq("library_video_id", videoId)
          .order("timestamp_start", { ascending: true });

        if (fetchError) throw fetchError;

        return (data as VideoAnnotation[]) || [];
      } catch (err: any) {
        console.error("[useVideoAnnotations] Error fetching video annotations:", err);
        return [];
      }
    },
    []
  );

  // Get single annotation
  const getAnnotation = useCallback(
    async (id: string): Promise<VideoAnnotation | null> => {
      try {
        const { data, error: fetchError } = await supabase
          .from("video_annotations")
          .select("*")
          .eq("id", id)
          .single();

        if (fetchError) throw fetchError;

        return data as VideoAnnotation;
      } catch (err: any) {
        console.error("[useVideoAnnotations] Error fetching annotation:", err);
        return null;
      }
    },
    []
  );

  // Create new annotation
  const createAnnotation = useCallback(
    async (input: CreateAnnotationInput): Promise<VideoAnnotation | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id || !organization?.id) {
        toast.error("You must be logged in to create annotations");
        return null;
      }

      // Validate that at least one video source is provided
      if (!input.library_video_id && !input.message_id && !input.source_video_url) {
        toast.error("A video source is required");
        return null;
      }

      try {
        const { data, error: createError } = await supabase
          .from("video_annotations")
          .insert({
            organization_id: organization.id,
            coach_id: user.id,
            client_id: input.client_id,
            library_video_id: input.library_video_id || null,
            message_id: input.message_id || null,
            source_video_url: input.source_video_url || null,
            title: input.title || null,
            annotation_data: input.annotation_data,
            timestamp_start: input.timestamp_start || null,
            timestamp_end: input.timestamp_end || null,
          })
          .select()
          .single();

        if (createError) throw createError;

        const newAnnotation = data as VideoAnnotation;
        setAnnotations((prev) => [newAnnotation, ...prev]);
        toast.success("Annotation saved");

        return newAnnotation;
      } catch (err: any) {
        console.error("[useVideoAnnotations] Error creating annotation:", err);
        toast.error(err.message || "Failed to save annotation");
        return null;
      }
    },
    [organization?.id]
  );

  // Update annotation
  const updateAnnotation = useCallback(
    async (id: string, input: UpdateAnnotationInput): Promise<VideoAnnotation | null> => {
      try {
        const { data, error: updateError } = await supabase
          .from("video_annotations")
          .update({
            ...(input.title !== undefined && { title: input.title }),
            ...(input.annotation_data !== undefined && {
              annotation_data: input.annotation_data,
            }),
            ...(input.timestamp_start !== undefined && {
              timestamp_start: input.timestamp_start,
            }),
            ...(input.timestamp_end !== undefined && {
              timestamp_end: input.timestamp_end,
            }),
          })
          .eq("id", id)
          .select()
          .single();

        if (updateError) throw updateError;

        const updatedAnnotation = data as VideoAnnotation;
        setAnnotations((prev) =>
          prev.map((a) => (a.id === id ? updatedAnnotation : a))
        );
        toast.success("Annotation updated");

        return updatedAnnotation;
      } catch (err: any) {
        console.error("[useVideoAnnotations] Error updating annotation:", err);
        toast.error(err.message || "Failed to update annotation");
        return null;
      }
    },
    []
  );

  // Delete annotation
  const deleteAnnotation = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from("video_annotations")
        .delete()
        .eq("id", id);

      if (deleteError) throw deleteError;

      setAnnotations((prev) => prev.filter((a) => a.id !== id));
      toast.success("Annotation deleted");

      return true;
    } catch (err: any) {
      console.error("[useVideoAnnotations] Error deleting annotation:", err);
      toast.error(err.message || "Failed to delete annotation");
      return false;
    }
  }, []);

  return {
    annotations,
    loading,
    error,
    fetchAnnotations,
    fetchAnnotationsByVideo,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
    getAnnotation,
  };
}

export default useVideoAnnotations;
